import { z } from "zod";
import { chatMessageSchema } from "./chat.js";
import { generationStatusSchema } from "./enums.js";

// ── Upload URL ─────────────────────────
export const uploadUrlRequestSchema = z.object({
  count: z.number().int().min(1).max(12),
  /** Optional content type override. Defaults to image/png to preserve the
   *  legacy behavior. Use "audio/mpeg" / "audio/wav" for voice clone samples. */
  contentType: z.enum(["image/png", "audio/mpeg", "audio/wav", "audio/mp4", "audio/m4a", "audio/aac", "audio/webm"]).optional(),
});
export type UploadUrlRequest = z.infer<typeof uploadUrlRequestSchema>;

export const uploadUrlResponseSchema = z.object({
  urls: z.array(
    z.object({
      presignedUrl: z.string(),
      publicUrl: z.string(),
    })
  ),
});
export type UploadUrlResponse = z.infer<typeof uploadUrlResponseSchema>;

// ── Improve Prompt ─────────────────────
export const improvePromptRequestSchema = z.object({
  prompt: z.string().min(1),
  mode: z.enum(["general", "inpaint", "ad", "video"]).optional(),
});
export type ImprovePromptRequest = z.infer<typeof improvePromptRequestSchema>;

export const improvePromptResponseSchema = z.object({
  success: z.boolean(),
  improvedPrompt: z.string().optional(),
  error: z.string().optional(),
});
export type ImprovePromptResponse = z.infer<typeof improvePromptResponseSchema>;

// ── Image Director ─────────────────────
export const imageDirectorRequestSchema = z.object({
  messages: z.array(chatMessageSchema),
});
export type ImageDirectorRequest = z.infer<typeof imageDirectorRequestSchema>;

export const imageDirectorResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  promptReady: z.boolean().optional(),
  error: z.string().optional(),
});
export type ImageDirectorResponse = z.infer<typeof imageDirectorResponseSchema>;

// ── Generate Video ─────────────────────
export const generateVideoRequestSchema = z.object({
  imageUrl: z.string().url().optional(),
  endImageUrl: z.string().url().optional(),
  prompt: z.string().min(1),
  modelId: z.string(),
  duration: z.union([z.literal(4), z.literal(5), z.literal(8), z.literal(10), z.literal(12)]),
  resolution: z.enum(["720p", "1080p"]),
  aspectRatio: z.string(),
  soundEnabled: z.boolean(),
  pipeline: z.string().optional(),
});
export type GenerateVideoRequest = z.infer<typeof generateVideoRequestSchema>;

export const generateVideoResponseSchema = z.object({
  success: z.boolean(),
  videoId: z.string().optional(),
  taskId: z.string().optional(),
  error: z.string().optional(),
  code: z.string().optional(),
});
export type GenerateVideoResponse = z.infer<typeof generateVideoResponseSchema>;

// ── Image Resolution ──────────────────
export const imageResolutionSchema = z.enum(["1k", "2k", "4k"]);
export type ImageResolution = z.infer<typeof imageResolutionSchema>;

// ── Edit Image ─────────────────────────
export const editImageRequestSchema = z.object({
  prompt: z.string().min(1),
  aspectRatio: z.string().min(1),
  resolution: imageResolutionSchema,
  modelId: z.string().optional(),
  quantity: z.number().int().min(1).max(4),
  imageUrl: z.string().optional(),
  maskUrl: z.string().optional(),
  referenceImageUrls: z.array(z.string()).optional(),
});
export type EditImageRequest = z.infer<typeof editImageRequestSchema>;

export const editImageResponseSchema = z.object({
  success: z.boolean(),
  pending: z.boolean().optional(),
  image: z.string().optional(),
  images: z.array(z.string()).optional(),
  error: z.string().optional(),
  code: z.string().optional(),
});
export type EditImageResponse = z.infer<typeof editImageResponseSchema>;

// ── Generate Ad Images (SSE) ───────────
export const generateAdRequestSchema = z.object({
  imageUrl: z.string().min(1),
  aspectRatio: z.string().min(1),
  backgroundColor: z.string().optional(),
  resolution: imageResolutionSchema,
  profileImageUrl: z.string().optional(),
  referenceImageUrls: z.array(z.string()).optional(),
  userPrompt: z.string().optional(),
  modelId: z.string().optional(),
});
export type GenerateAdRequest = z.infer<typeof generateAdRequestSchema>;

// ── Generate From Director ─────────────
export const generateFromDirectorRequestSchema = z.object({
  messages: z.array(chatMessageSchema),
  quantity: z.number().int().min(1).max(10).optional(),
  resolution: imageResolutionSchema,
  aspectRatio: z.string().min(1),
  referenceImageUrls: z.array(z.string()).optional(),
  modelId: z.string().optional(),
});
export type GenerateFromDirectorRequest = z.infer<typeof generateFromDirectorRequestSchema>;

export const generateFromDirectorResponseSchema = z.object({
  success: z.boolean(),
  pending: z.boolean().optional(),
  images: z.array(z.string()).optional(),
  promptUsed: z.string().optional(),
  error: z.string().optional(),
  code: z.string().optional(),
});
export type GenerateFromDirectorResponse = z.infer<typeof generateFromDirectorResponseSchema>;

// ── Check Generation Status (unified) ─────
export const checkGenerationStatusRequestSchema = z.object({
  id: z.string(),
  type: z.enum(["image", "video"]),
});
export type CheckGenerationStatusRequest = z.infer<typeof checkGenerationStatusRequestSchema>;

export const checkGenerationStatusResponseSchema = z.object({
  success: z.boolean(),
  status: generationStatusSchema,
  imageUrl: z.string().optional(),
  videoUrl: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  error: z.string().optional(),
});
export type CheckGenerationStatusResponse = z.infer<typeof checkGenerationStatusResponseSchema>;

// ── Admin Pricing Analytics ───────────
const dailyStatSchema = z.object({
  date: z.string(),
  revenue: z.number(),
  cost: z.number(),
  margin: z.number(),
  generations: z.number(),
});

const modelStatSchema = z.object({
  model: z.string(),
  revenue: z.number(),
  cost: z.number(),
  margin: z.number(),
  generations: z.number(),
});

const typeStatSchema = z.object({
  type: z.enum(["image", "video"]),
  revenue: z.number(),
  cost: z.number(),
  margin: z.number(),
  generations: z.number(),
});

export const adminPricingAnalyticsResponseSchema = z.object({
  dailyStats: z.array(dailyStatSchema),
  modelStats: z.array(modelStatSchema),
  typeStats: z.array(typeStatSchema),
});
export type AdminPricingAnalyticsResponse = z.infer<typeof adminPricingAnalyticsResponseSchema>;

// ── Admin Customer Profitability ──────
/** One paying user's lifetime usage-vs-payment profitability. All monetary
 *  fields are in ILS (₪); provider cost is converted from USD via USD_TO_ILS. */
const customerProfitRowSchema = z.object({
  userId: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
  plan: z.string(),
  planStatus: z.string(),
  totalPaidIls: z.number(),
  generations: z.number(),
  creditsUsed: z.number(),
  providerCostIls: z.number(),
  profitIls: z.number(),
  marginPct: z.number(),
});

export const adminCustomerProfitResponseSchema = z.object({
  customers: z.array(customerProfitRowSchema),
  totals: z.object({
    payingUsers: z.number(),
    revenueIls: z.number(),
    providerCostIls: z.number(),
    profitIls: z.number(),
    avgProfitIls: z.number(),
    /** Share of generations whose provider cost is Runware's real billed
     *  `api_cost` (vs estimated from credit_cost). Lower = trust the numbers
     *  less. 100 when there are no generations. */
    measuredCostPct: z.number(),
  }),
  fxRate: z.number(),
});
export type AdminCustomerProfitResponse = z.infer<typeof adminCustomerProfitResponseSchema>;
export type CustomerProfitRow = z.infer<typeof customerProfitRowSchema>;

// ── Profile Photo (Headshot) ──────────
export const profilePhotoGenderSchema = z.enum(["male", "female"]);
export type ProfilePhotoGender = z.infer<typeof profilePhotoGenderSchema>;

/** Image models the Profile Photo flow can generate with. */
export const profilePhotoImageModelIdSchema = z.enum([
  "nano-banana-2",
  "gpt-image-2",
  "gemini-3-pro",
]);
export type ProfilePhotoImageModelId = z.infer<typeof profilePhotoImageModelIdSchema>;

/** Canonical fixed model set: one image per model, in one click. Both server
 *  and frontend must agree on this exact list. */
export const PROFILE_PHOTO_MODEL_SET = [
  "gpt-image-2",
  "gemini-3-pro",
  "nano-banana-2",
] as const satisfies readonly ProfilePhotoImageModelId[];

/** Flat, flow-specific price for one Profile Photo generation (the 3-image
 *  set above, locked to 1k). This is an INTENTIONAL exception to canonical
 *  per-model pricing — the flow is sold as a fixed bundle. Server charges it,
 *  frontend displays it; they must match. */
export const PROFILE_PHOTO_FLOW_CREDIT_COST = 50;

export const generateProfilePhotoRequestSchema = z.object({
  presetId: z.string().min(1),
  gender: profilePhotoGenderSchema,
  sourceImageUrls: z.array(z.string().url()).min(2).max(3),
  aspectRatio: z.string().min(1),
  resolution: imageResolutionSchema,
  /** Optional hairstyle override. When provided, replaces the default
   *  "keep hair exactly as in the reference" instruction in the prompt's
   *  [HAIR] section. */
  customHairstyle: z.string().max(300).optional(),
  /** Custom-preset only: user-supplied clothing description, injected into the
   *  prompt's [CLOTHING] section. Required when a custom preset is selected
   *  (enforced server-side in the service). */
  customClothing: z.string().max(200).optional(),
  /** Custom-preset only: user-supplied background description, injected into the
   *  prompt's [BACKGROUND] section. Required when a custom preset is selected. */
  customBackground: z.string().max(200).optional(),
  /** Models to generate with — one image per model. */
  imageModelIds: z.array(profilePhotoImageModelIdSchema).min(1).max(3),
});
export type GenerateProfilePhotoRequest = z.infer<typeof generateProfilePhotoRequestSchema>;

export const generateProfilePhotoResponseSchema = z.object({
  success: z.boolean(),
  images: z.array(z.string().url()),
  appearance: z.string().optional(),
});
export type GenerateProfilePhotoResponse = z.infer<typeof generateProfilePhotoResponseSchema>;

export const profilePhotoPresetsResponseSchema = z.object({
  presets: z.array(
    z.object({
      id: z.string(),
      gender: profilePhotoGenderSchema,
      nameKey: z.string(),
      previewImage: z.string(),
    }),
  ),
});
export type ProfilePhotoPresetsResponse = z.infer<typeof profilePhotoPresetsResponseSchema>;

// ── Talking Avatar ─────────────────────
export const talkingAvatarSubjectTypeSchema = z.enum(["person", "object"]);
export type TalkingAvatarSubjectType = z.infer<typeof talkingAvatarSubjectTypeSchema>;
export const talkingAvatarStyleSchema = z.enum(["realistic", "cartoon"]);
export type TalkingAvatarStyle = z.infer<typeof talkingAvatarStyleSchema>;
export const talkingAvatarTierSchema = z.enum(["standard", "pro"]);
export type TalkingAvatarTier = z.infer<typeof talkingAvatarTierSchema>;

/** Image models surfaced in the Talking Avatar bottom-bar picker. Affects
 *  only the Stage-1 preview pair (NOT the Stage-2 Kling Avatar video). */
export const talkingAvatarImageModelIdSchema = z.enum(["nano-banana-2", "gpt-image-2"]);
export type TalkingAvatarImageModelId = z.infer<typeof talkingAvatarImageModelIdSchema>;

export const previewTalkingAvatarRequestSchema = z.object({
  sourceImageUrl: z.string().url(),
  subjectType: talkingAvatarSubjectTypeSchema,
  style: talkingAvatarStyleSchema,
  backgroundDescription: z.string().max(500),
  /** Optional composition details (pose, props, framing) appended to the prompt
   *  when the user opens the "Advanced instructions" panel. */
  composition: z.string().max(500).optional(),
  /** User's image-model choice from the bottom-bar picker. Falls back to the
   *  server-side default (`PREVIEW_MODEL_ID`) when omitted. */
  imageModelId: talkingAvatarImageModelIdSchema.optional(),
});
export type PreviewTalkingAvatarRequest = z.infer<typeof previewTalkingAvatarRequestSchema>;

export const previewTalkingAvatarResponseSchema = z.object({
  success: z.boolean(),
  imageUrls: z.array(z.string().url()),
});
export type PreviewTalkingAvatarResponse = z.infer<typeof previewTalkingAvatarResponseSchema>;

/**
 * Two render modes:
 *  - "TTS mode" (default): {dialogue, voiceId} — the avatar speaks the text in
 *    the chosen voice via ElevenLabs.
 *  - "BYO-audio mode": {audioUrl, audioDurationSec} — user-recorded/uploaded
 *    audio is fed directly to lip-sync, skipping TTS entirely. Gated to
 *    Starter+ in the service. Max 30 seconds enforced in `audioDurationSec`.
 */
export const renderTalkingAvatarRequestSchema = z
  .object({
    imageUrl: z.string().url(),
    dialogue: z.string().min(1).optional(),
    voiceId: z.string().min(1).optional(),
    audioUrl: z.string().url().optional(),
    // Min 2 s: Kling rejects shorter audio and it'd be mostly silence-pad.
    // Mirrors BYO_AUDIO_MIN/MAX_DURATION_SEC in generateTalkingAvatarService.
    audioDurationSec: z.number().min(2).max(30).optional(),
    tier: talkingAvatarTierSchema,
    language: z.string().optional(),
    /** Optional motion/action prompt forwarded to Kling Avatar's `positivePrompt`.
     *  Guides expressive cues while the character speaks (e.g. "wave hand",
     *  "lean forward and smile"). Max 500 chars; Runware caps at 2500. */
    motion: z.string().max(500).optional(),
  })
  .refine(
    (v) =>
      (v.dialogue && v.voiceId && !v.audioUrl) ||
      (v.audioUrl && v.audioDurationSec && !v.dialogue && !v.voiceId),
    {
      message:
        "Provide either {dialogue,voiceId} for TTS OR {audioUrl,audioDurationSec} for BYO-audio — not both.",
    },
  );
export type RenderTalkingAvatarRequest = z.infer<typeof renderTalkingAvatarRequestSchema>;

export const renderTalkingAvatarResponseSchema = z.object({
  success: z.boolean(),
  videoId: z.string(),
  videoUrl: z.string().url(),
});
export type RenderTalkingAvatarResponse = z.infer<typeof renderTalkingAvatarResponseSchema>;

// Clone voice — POST /api/talking-avatar/clone-voice
// Client uploads the audio sample to R2 via /api/upload-url first (with
// contentType audio/*), then submits the publicUrl here. Server fetches the
// sample and forwards to ElevenLabs IVC.
export const cloneVoiceRequestSchema = z.object({
  sampleUrl: z.string().url(),
  name: z.string().min(1).max(50),
  gender: z.enum(["male", "female"]),
});
export type CloneVoiceRequest = z.infer<typeof cloneVoiceRequestSchema>;

export const cloneVoiceResponseSchema = z.object({
  success: z.boolean(),
  voice: z.object({
    id: z.string(),
    name: z.string(),
    gender: z.enum(["male", "female"]),
    previewUrl: z.string().url(),
  }),
});
export type CloneVoiceResponse = z.infer<typeof cloneVoiceResponseSchema>;

// Public voice-preset metadata (no API IDs leaked).
export const talkingAvatarVoicesResponseSchema = z.object({
  voices: z.array(
    z.object({
      id: z.string(),
      nameKey: z.string(),
      gender: z.enum(["male", "female"]),
      language: z.enum(["en", "he", "multilingual"]),
      /** CDN URL of a Hebrew sample MP3 the user can play in the picker. */
      previewUrl: z.string().url(),
      /** Marks user-cloned voices (vs. system presets). */
      isCloned: z.boolean().optional(),
    }),
  ),
  /** Echoed back so the UI knows whether the cloning feature is unlocked. */
  canCloneVoice: z.boolean(),
  /** Max dialogue words allowed for THIS user (resolved by plan). UI uses
   *  this to gate the textarea and show the upgrade modal at the boundary. */
  maxWords: z.number().int().positive(),
  /** Minimum dialogue words. Below this Kling Avatar rejects the audio for
   *  being shorter than its 2-second floor — UI gates the Generate button
   *  on this and shows the min-words hint. */
  minWords: z.number().int().positive(),
});
export type TalkingAvatarVoicesResponse = z.infer<typeof talkingAvatarVoicesResponseSchema>;

// ── Product Creative ──────────────────
export const generateProductCreativeRequestSchema = z.object({
  sourceImageUrls: z.array(z.string().url()).min(1).max(3),
  imageCount: z.number().int().min(1).max(10),
  aspectRatio: z.string().min(1),
  resolution: imageResolutionSchema,
  /** Optional creative override — user describes the background + elements
   *  that should fly around the product. Replaces the auto-decided dominant
   *  color background + key-ingredients elements when provided. */
  customDirection: z.string().max(2000).optional(),
});
export type GenerateProductCreativeRequest = z.infer<typeof generateProductCreativeRequestSchema>;

export const generateProductCreativeResponseSchema = z.object({
  success: z.boolean(),
  images: z.array(z.string().url()),
  analysis: z.string().optional(),
});
export type GenerateProductCreativeResponse = z.infer<typeof generateProductCreativeResponseSchema>;

// ── Baseball Trend (Trends tab) ──────────
export const baseballTrendPresetIdSchema = z.enum([
  "baseball",
  "soccer",
  "concert",
  "basketball",
]);
export type BaseballTrendPresetId = z.infer<typeof baseballTrendPresetIdSchema>;

/** Kling 3.0 Pro only supports 5s and 10s — keep this in sync with
 *  server/src/config/baseballTrend.ts `BASEBALL_TREND_DURATIONS`. */
export const baseballTrendDurationSchema = z.union([z.literal(5), z.literal(10)]);
export type BaseballTrendDuration = z.infer<typeof baseballTrendDurationSchema>;

/** Aspect ratios — both stages (image + video) use the same one for
 *  consistent framing. Mirror of `BASEBALL_TREND_ASPECT_RATIOS`. */
export const baseballTrendAspectRatioSchema = z.enum(["16:9", "9:16"]);
export type BaseballTrendAspectRatio = z.infer<typeof baseballTrendAspectRatioSchema>;

/** Image-generation models the user can pick for the preview stage. Mirror of
 *  `BASEBALL_TREND_IMAGE_MODEL_IDS`. */
export const baseballTrendImageModelIdSchema = z.enum(["nano-banana-2", "gpt-image-2"]);
export type BaseballTrendImageModelId = z.infer<typeof baseballTrendImageModelIdSchema>;

// ── Stage 1 — preview (2 broadcast images for the user to pick from) ──
export const previewBaseballTrendRequestSchema = z.object({
  presetId: baseballTrendPresetIdSchema,
  /** R2 public URL of the user's uploaded face/body photo. */
  sourceImageUrl: z.string().url(),
  /** Output aspect ratio. Same ratio is used for both stages so the
   *  preview that's picked stays consistent in the final video. */
  aspectRatio: baseballTrendAspectRatioSchema,
  /** Optional wardrobe/props override — replaces the per-preset default.
   *  When omitted, the server picks a wardrobe that matches the scene. */
  customWardrobe: z.string().max(1000).optional(),
  /** Which image model to use for the preview pair. Omit to use the
   *  server-side default (currently Nano Banana 2). */
  imageModelId: baseballTrendImageModelIdSchema.optional(),
});
export type PreviewBaseballTrendRequest = z.infer<typeof previewBaseballTrendRequestSchema>;

export const previewBaseballTrendResponseSchema = z.object({
  success: z.boolean(),
  /** Public R2 URLs of the N broadcast preview images. Length matches
   *  `BASEBALL_TREND_PREVIEW_COUNT` on the server (currently 2). */
  previewImageUrls: z.array(z.string().url()).min(1),
});
export type PreviewBaseballTrendResponse = z.infer<typeof previewBaseballTrendResponseSchema>;

// ── Stage 2 — render (animate the selected preview into a video) ──
export const renderBaseballTrendRequestSchema = z.object({
  /** Public R2 URL of the preview the user picked in stage 1. */
  selectedPreviewUrl: z.string().url(),
  duration: baseballTrendDurationSchema,
  /** Carried over from the preview step — needed so the video matches the
   *  shape of the selected image. */
  aspectRatio: baseballTrendAspectRatioSchema,
  /** Carried over so the gallery row remembers which scene was used. */
  presetId: baseballTrendPresetIdSchema,
  /** Optional motion override — describes what the character does in the
   *  video. When omitted the server uses the universal stage-2 prompt. */
  customAction: z.string().max(1000).optional(),
});
export type RenderBaseballTrendRequest = z.infer<typeof renderBaseballTrendRequestSchema>;

export const renderBaseballTrendResponseSchema = z.object({
  success: z.boolean(),
  /** Database id of the pending generated_videos row. The frontend uses
   *  this to follow status via Supabase realtime. */
  videoId: z.string(),
  /** Provider task id (Runware / Kling). Mostly useful for debugging. */
  taskId: z.string(),
});
export type RenderBaseballTrendResponse = z.infer<typeof renderBaseballTrendResponseSchema>;

export const baseballTrendPresetsResponseSchema = z.object({
  presets: z.array(
    z.object({
      id: baseballTrendPresetIdSchema,
      nameKey: z.string(),
      previewImage: z.string(),
    }),
  ),
});
export type BaseballTrendPresetsResponse = z.infer<typeof baseballTrendPresetsResponseSchema>;

// ── Track Signup ───────────────────────
export const trackSignupRequestSchema = z.object({
  method: z.enum(["email", "google"]).optional(),
  /** Referral code captured from a /r/:code link, forwarded so the server can
   *  attribute the new user to their referrer. */
  referralCode: z.string().optional(),
});
export type TrackSignupRequest = z.infer<typeof trackSignupRequestSchema>;

export const trackSignupResponseSchema = z.object({
  tracked: z.boolean(),
  /** Present only when a referralCode was supplied. Lets the client show the
   *  "you got N bonus credits" celebration toast. */
  referral: z
    .object({
      attributed: z.boolean(),
      refereeBonusCredits: z.number().optional(),
      referrerName: z.string().optional(),
    })
    .optional(),
});
export type TrackSignupResponse = z.infer<typeof trackSignupResponseSchema>;

// ── Referral ───────────────────────────
/** GET /api/referral/:code — public code resolution for the /r/:code page. */
export const resolveReferralResponseSchema = z.object({
  valid: z.boolean(),
  referrerName: z.string().optional(),
  refereeBonusCredits: z.number().optional(),
});
export type ResolveReferralResponse = z.infer<typeof resolveReferralResponseSchema>;

/** GET /api/referral/stats — the signed-in user's referral dashboard data. */
export const referralStatsResponseSchema = z.object({
  referralCode: z.string(),
  /** Friends who signed up via this user's link. */
  joinedCount: z.number(),
  /** Referred friends who became paying subscribers. */
  subscribedCount: z.number(),
  /** Total referral credits this user has earned. */
  creditsEarned: z.number(),
  /** Reward amounts (server-owned single source of truth — never hardcode). */
  refereeBonusCredits: z.number(),
  referrerRewardCredits: z.number(),
  /** ISO timestamp of the most recent reward, or null. Drives the "you earned"
   *  toast (client compares against a localStorage "last seen" value). */
  latestRewardedAt: z.string().nullable(),
});
export type ReferralStatsResponse = z.infer<typeof referralStatsResponseSchema>;

/** GET /api/referral/pending — referee-side: does the signed-in user have a
 *  pending referral whose bonus unlocks when they subscribe to a paid plan?
 *  Drives the persistent "subscribe → you both get N" banner. */
export const refereePendingBonusResponseSchema = z.object({
  /** True when the user was referred and hasn't yet been rewarded. */
  pending: z.boolean(),
  /** Bonus credits the user (and their referrer) each get on first paid sub. */
  bonusCredits: z.number().optional(),
  /** The referrer's display name, when available. */
  referrerName: z.string().optional(),
});
export type RefereePendingBonusResponse = z.infer<typeof refereePendingBonusResponseSchema>;

// ── Bot Builder ────────────────────────────────────────────────────────────
// Per-user custom AI chatbot that the owner embeds on their own website.

/** POST /api/bots — create the owner's single bot from a homepage URL. */
export const createBotRequestSchema = z.object({
  name: z.string().min(1).max(80),
  sourceUrl: z.string().url().max(2000),
  /** Domain where the iframe is allowed to load (server checks Origin). */
  allowedDomain: z.string().min(3).max(253),
  /** Optional ≤30-word example of the bot's voice. Counted server-side. */
  speakingStyleSample: z.string().max(500).default(""),
  /** Optional free-form additional info (≤4000 chars). */
  extraInfo: z.string().max(4000).default(""),
});
export type CreateBotRequest = z.infer<typeof createBotRequestSchema>;

/** PATCH /api/bots/mine — partial edit of the owner's bot. */
export const updateBotRequestSchema = z
  .object({
    name: z.string().min(1).max(80).optional(),
    allowedDomain: z.string().min(3).max(253).optional(),
    speakingStyleSample: z.string().max(500).optional(),
    extraInfo: z.string().max(4000).optional(),
    /** When true, re-scrape the source URL and rebuild the system prompt. */
    reScrape: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.allowedDomain !== undefined ||
      v.speakingStyleSample !== undefined ||
      v.extraInfo !== undefined ||
      v.reScrape === true,
    { message: "At least one field must be provided" },
  );
export type UpdateBotRequest = z.infer<typeof updateBotRequestSchema>;

/** Owner-facing bot view returned by GET /api/bots/mine and the create/update
 *  endpoints. `systemPrompt` is intentionally NOT returned — keeping it
 *  server-side reduces the attack surface for prompt-leak attempts.
 *
 *  Wallet fields: `botCreditBalance` is the current prepaid balance (visitor
 *  messages decrement this, NOT main credits). `totalDeposited` is the
 *  lifetime sum of deposits — used for the depletion progress bar
 *  (balance / totalDeposited). Both default to 0 for brand-new bots that
 *  haven't been topped up yet. */
export const userBotResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  sourceUrl: z.string(),
  allowedDomain: z.string(),
  speakingStyleSample: z.string(),
  extraInfo: z.string(),
  scrapedData: z.record(z.string(), z.unknown()),
  status: z.enum(["active", "paused", "deleted"]),
  botCreditBalance: z.number().int().nonnegative(),
  totalDeposited: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type UserBotResponse = z.infer<typeof userBotResponseSchema>;

/** POST /api/bots/mine/deposit — owner moves credits from their main wallet
 *  into their bot's wallet. The pack variant picks the amount (1:1, no bonus
 *  on larger packs — see credit_costs.bot_pack rows). Server reads the cost
 *  from credit_costs; the client never sends the amount. */
export const botDepositPackSchema = z.enum(["small", "medium", "large", "xl"]);
export type BotDepositPack = z.infer<typeof botDepositPackSchema>;

export const depositToBotRequestSchema = z.object({
  pack: botDepositPackSchema,
});
export type DepositToBotRequest = z.infer<typeof depositToBotRequestSchema>;

export const depositToBotResponseSchema = z.object({
  botCreditBalance: z.number().int().nonnegative(),
  totalDeposited: z.number().int().nonnegative(),
});
export type DepositToBotResponse = z.infer<typeof depositToBotResponseSchema>;

/** POST /api/bot-embed/:botId/message — visitor chat. SSE response. */
export const botEmbedMessageRequestSchema = z.object({
  history: z
    .array(
      z.object({
        role: z.enum(["user", "assistant"]),
        content: z.string().min(1).max(2000),
      }),
    )
    .min(1)
    .max(20),
});
export type BotEmbedMessageRequest = z.infer<typeof botEmbedMessageRequestSchema>;
